const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
/**
 * What a dialog owes the keyboard: Tab stays inside it (from the last control to the first and back),
 * Escape answers it, and when it closes the focus goes back to what opened it. aria-modal tells a screen
 * reader the page behind is out of play; this is what makes it so for someone who tabs.
 *
 * Call it when the dialog has been drawn and keep the function it returns: calling that takes the
 * listener away and gives the focus back. A dialog that takes the focus itself while it is drawn (autoFocus)
 * has to say who had it before, since by the time this is called that is no longer known: pass `opener`,
 * taken when the component is made.
 */
export function activateDialog(getDialog, onEscape, opener = document.activeElement) {
    const onKey = (e) => {
        if (e.key === "Escape") {
            onEscape();
            return;
        }
        if (e.key !== "Tab")
            return;
        const dialog = getDialog();
        if (!dialog)
            return;
        const items = Array.from(dialog.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
        if (items.length === 0) {
            e.preventDefault();
            dialog.focus();
            return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (!dialog.contains(active)) {
            e.preventDefault();
            first.focus();
        }
        else if (e.shiftKey && (active === first || active === dialog)) {
            e.preventDefault();
            last.focus();
        }
        else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
        }
    };
    document.addEventListener("keydown", onKey);
    return () => {
        document.removeEventListener("keydown", onKey);
        // The opener may be gone by now (a button that took the shift off the list): then there is nothing to give it back to.
        if (opener instanceof HTMLElement && opener !== document.body && document.contains(opener))
            opener.focus();
    };
}
